import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { computeBackoffDelayMs } from "@/helpers/auth-retry.helpers";
import {
  evaluateContext,
  evaluateLiveSet,
  existingSlots,
  getCompanyFiles,
  getEvaluationStatus,
  getLatestQuestionSet,
  getLatestRunForSet,
  getLiveSetEvaluation,
  getPreviousRun,
  liveFilesBySlot,
  publishContext,
  startEvaluationRun,
  validateQuestions,
  type CompanyFileSummary,
  type ContextFileKey,
  type EvaluationItemResponse,
  type GovernedEvaluationJobResponse,
  type PickedContextFiles,
  type PublishContextOptions,
  type QuestionRow,
  type TierResult,
} from "@/services/apis/contextConsoleAPI";

const COMPANY_FILES_KEY = ["company-files"] as const;
const LIVE_SET_EVALUATION_KEY = ["live-set-evaluation"] as const;
const LATEST_QUESTION_SET_KEY = ["latest-question-set"] as const;
const POLL_INTERVAL_MS = 3000;
/**
 * Job state is DB-backed, so a 404 means the job genuinely doesn't exist (a
 * stale id from a previous browser session, or the row was never created) —
 * not a worker that hasn't caught up yet. A couple of retries absorbs a
 * request that lands mid-commit; past that it's not coming back.
 */
const NOT_FOUND_RETRIES = 2;
/**
 * A governed run answers every question twice (primary + fallback tier),
 * takes minutes, and costs real LLM calls per question — abandoning it on the
 * first network blip or 502 mid-poll (~100 requests over a 5-minute run) would
 * force a full, expensive re-run for a job that's still fine server-side.
 * A handful of backed-off attempts absorbs that without polling forever.
 */
const TRANSIENT_ERROR_RETRIES = 4;

const isTransientError = (error: unknown): boolean => {
  const status = (error as AxiosError)?.response?.status;
  // No response at all (network drop, timeout) or a retriable server status —
  // this has nothing to do with whether the job itself is still running. A
  // 404 is handled separately: DB-backed job state means a 404 is a real
  // "doesn't exist", not a worker lagging behind, so it isn't treated as
  // transient here.
  return status === undefined || status >= 500 || status === 429;
};

/**
 * The context set IQ is answering from right now, for the page header chip.
 *
 * Hydrated from `GET /files` on load and re-read after publish, so the chip
 * always reflects server truth rather than a locally-stitched copy.
 *
 * `enabled` defaults to true for callers that already know the viewer can
 * reach Context Console (the page itself). Callers that render regardless of
 * permission (e.g. Settings' section nav, which needs `liveSet` only to
 * badge a row the viewer might not even have) must pass `enabled: false` for
 * everyone else — `GET /files` 403s for a non-admin, and the app's global
 * error handler toasts every 403.
 */
export const useLiveSet = (enabled = true) => {
  const { data, isLoading, error } = useQuery({
    queryKey: COMPANY_FILES_KEY,
    queryFn: getCompanyFiles,
    select: (response) => response.live_set,
    staleTime: 60_000,
    enabled,
  });

  return { liveSet: data ?? null, isLoading, error };
};

/**
 * Slots the company already has a live file for.
 *
 * Shares `COMPANY_FILES_KEY` with {@link useLiveSet} — same query, same cache
 * entry, no extra request — just a narrower `select`. Used to work out which
 * unpicked slots are actually fine to submit (backend will fill them from the
 * live file) versus genuinely missing.
 */
export const useExistingSlots = () => {
  const { data } = useQuery({
    queryKey: COMPANY_FILES_KEY,
    queryFn: getCompanyFiles,
    select: (response) => existingSlots(response.files),
    staleTime: 60_000,
  });

  return data ?? new Set<ContextFileKey>();
};

const EMPTY_LIVE_FILES: Record<ContextFileKey, CompanyFileSummary[]> = {
  business_logic: [],
  defaults: [],
  sql_templates: [],
  yamls: [],
  all_tables: [],
};

/**
 * The live file (name, size, hash, last-updated) for each of the five slots,
 * so the upload stage can show what's actually live instead of always
 * starting blank.
 *
 * Shares `COMPANY_FILES_KEY` with {@link useLiveSet}/{@link useExistingSlots}
 * — same query, same cache entry, just a differently-shaped `select`.
 */
export const useLiveFileDetails = () => {
  const { data } = useQuery({
    queryKey: COMPANY_FILES_KEY,
    queryFn: getCompanyFiles,
    select: (response) => liveFilesBySlot(response.files),
    staleTime: 60_000,
  });

  return data ?? EMPTY_LIVE_FILES;
};

/** Score the five picked files. Writes nothing — publish is the only write. */
export const useEvaluateContext = () =>
  useMutation({ mutationFn: (files: PickedContextFiles) => evaluateContext(files) });

/**
 * Publish the set and make it live.
 *
 * A gate-failing set rejects with 409 carrying fresh scores unless the caller
 * passes `overrideGate: true`, in which case the backend publishes anyway and
 * the receipt's `gate_cleared: false` says so. On a genuine 409 the caller
 * pulls the scores out with `gateFailPayload` and re-renders the rows. On
 * success the live-set query is invalidated so the header chip updates from
 * the server.
 */
export const usePublishContext = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ files, ...options }: { files: PickedContextFiles } & PublishContextOptions) =>
      publishContext(files, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPANY_FILES_KEY });
      // A republish moves `publishedSetId` on: the OLD set's cached
      // evaluation (keyed on the old id) must not go on being read as the
      // new live set's own score. Un-suffixed key invalidates every
      // setId variant.
      queryClient.invalidateQueries({ queryKey: LIVE_SET_EVALUATION_KEY });
    },
  });
};

/**
 * Backfill a real score for a set published via skip-evaluation (CC-B2).
 * Same live-set query invalidation as publish — the header chip and the
 * pending badge both read off it.
 */
export const useEvaluateLiveSet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => evaluateLiveSet(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPANY_FILES_KEY });
      queryClient.invalidateQueries({ queryKey: LIVE_SET_EVALUATION_KEY });
    },
  });
};

/**
 * The live set's already-published evaluation (full per-file breakdown),
 * for a caller that has no in-memory evaluation of its own — a fresh
 * session, or one that landed on Evaluate & publish without ever running
 * evaluate in this tab. `enabled` lets the caller skip this entirely once
 * it already has something to show (this session's own evaluate/live-set
 * result always wins — see ContextConsole.tsx). 404 ("nothing published
 * yet, or published via skipEvaluation and never backfilled") settles
 * immediately, no retries.
 *
 * Keyed on `setId` so a republish (a new `publishedSetId`) can never read
 * back the OLD set's cached evaluation under the new one's identity — the
 * cache entry for the old id is simply a different key, never stale data
 * masquerading as fresh. `usePublishContext`/`useEvaluateLiveSet` also
 * invalidate this (un-suffixed, so every setId variant) on success, so a
 * refetch replaces it rather than leaving it to expire on its own.
 */
export const useLiveSetEvaluation = (setId: string | null | undefined, enabled: boolean) => {
  const { data } = useQuery({
    queryKey: [...LIVE_SET_EVALUATION_KEY, setId],
    queryFn: getLiveSetEvaluation,
    enabled,
    // The score itself only ever changes on a publish or a backfill, and both
    // already invalidate this key — nothing else moves it, so there is
    // nothing worth a background refetch on remount/refocus for.
    staleTime: 60_000,
    retry: (failureCount, queryError) =>
      (queryError as AxiosError)?.response?.status !== 404 && failureCount < NOT_FOUND_RETRIES,
  });
  // A response naming a DIFFERENT set than the one just asked about is a
  // stale read racing a republish (a slower request for the old id settling
  // after a newer one already moved `setId` on) — never shown as this set's
  // own score.
  return data && (data.set_id === undefined || data.set_id === setId) ? data : null;
};

/**
 * Check a question set is regression-shaped before it is worth running. The
 * backend persists the submitted set as the company's latest on a pass
 * (`app/api/context_console.py`'s `validate_questions`), so a success
 * invalidates {@link useLatestQuestionSet}'s cache — otherwise a remount
 * within its `gcTime` would hydrate the OLDER cached set instead of the one
 * that was just validated.
 */
export const useValidateQuestions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (questions: QuestionRow[]) => validateQuestions(questions),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LATEST_QUESTION_SET_KEY }),
  });
};

/**
 * Start an accuracy run; the returned `job_id` drives {@link useEvaluationRun}.
 * Same persistence contract as {@link useValidateQuestions} — a successful
 * run also saves the set as the company's latest, so this invalidates the
 * same cache.
 */
export const useStartEvaluationRun = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ questions, contextSetId }: { questions: QuestionRow[]; contextSetId: string }) =>
      startEvaluationRun(questions, contextSetId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LATEST_QUESTION_SET_KEY }),
  });
};

/** One question's outcome, as the results list renders it. */
export interface QuestionOutcome {
  question: string;
  answer: string;
  /** Why it landed here — unset for a correct answer, which needs no reason. */
  reason: string | null;
  /** The chat's public uuid — links to /chat/{chatId}. */
  chatId: string | null;
}

/** One provider tier's numbers, as the accuracy screen renders them. */
export interface TierView {
  models: Record<string, string>;
  total: number;
  accuracy: number;
  correct: QuestionOutcome[];
  incorrect: QuestionOutcome[];
  /** No ground truth to compare, or the question errored before it could be scored. */
  unsuccessful: QuestionOutcome[];
  /** Real LLM cost of this tier's run, straight off TierMetrics. */
  totalCostUsd: number | null;
}

/** What the accuracy screen actually renders, derived from the job payload.
 * Test & Run answers each question once, on the primary tier — `fallback` is
 * null (no fallback-tier pass, no cross-provider comparison to show). */
export interface EvaluationRunView {
  status: GovernedEvaluationJobResponse["status"];
  error: string | null;
  isSettled: boolean;
  contextSetId: string | null;
  primary: TierView;
  fallback: TierView | null;
  /** Fraction 0..1 — how often primary and fallback agreed on pass/fail. */
  crossProviderConsistency: number | null;
  /** Questions the primary tier has finished so far; `itemsTotal` is 0 when unknown. */
  primaryItemsCompleted: number;
  itemsTotal: number;
  /** This run's own job id — lets a caller exclude it when looking up
   * whatever completed run came before it (usePreviousRun). */
  jobId: string | null;
  /** When this run last changed status — null only for a job just queued
   * this same request (never true for a settled one). */
  updatedAt: string | null;
}

const isSettled = (status?: GovernedEvaluationJobResponse["status"]) =>
  status === "completed" || status === "failed";

const EMPTY_TIER: TierView = {
  models: {},
  total: 0,
  accuracy: 0,
  correct: [],
  incorrect: [],
  unsuccessful: [],
  totalCostUsd: null,
};

/** Why an item landed in Incorrect/Unsuccessful — the item's own error takes
 * priority (a hard stream failure explains itself); otherwise fall back to
 * eval_error (e.g. "no ground truth"), then a generic pointer at the mismatch. */
const outcomeReason = (item: EvaluationItemResponse): string | null => {
  if (item.error_message) return item.error_message;
  if (item.evaluation?.eval_error) return item.evaluation.eval_error;
  if (item.evaluation?.actual_result_json) {
    // Backend naming trap: actual_result_json is the resolved *ground
    // truth* (from standard_sql or the provided expected_answer), not IQ's
    // own answer — "Expected …" here is correct, not a mislabel.
    return `Expected ${item.evaluation.actual_result_json}`;
  }
  return null;
};

const outcomeFromItem = (item: EvaluationItemResponse, reason: string | null): QuestionOutcome => ({
  question: item.question,
  answer: item.raw_answer,
  reason,
  chatId: item.chat_id ?? null,
});

const tierView = (tier: TierResult): TierView => {
  const total = tier.response.total_questions;
  const correct: QuestionOutcome[] = [];
  const incorrect: QuestionOutcome[] = [];
  const unsuccessful: QuestionOutcome[] = [];

  for (const item of tier.response.results) {
    const status = item.evaluation?.quantitative_status;
    if (status === "PASS") {
      correct.push(outcomeFromItem(item, null));
    } else if (status === "FAIL") {
      incorrect.push(outcomeFromItem(item, outcomeReason(item)));
    } else {
      // UNSUCCESSFUL, or no verdict at all (a stream error before scoring ran).
      unsuccessful.push(outcomeFromItem(item, outcomeReason(item)));
    }
  }

  return {
    models: tier.models,
    total,
    accuracy: total > 0 ? (correct.length / total) * 100 : 0,
    correct,
    incorrect,
    unsuccessful,
    totalCostUsd: tier.metrics.total_cost_usd,
  };
};

/** Narrows a raw job response to what the accuracy screen shows: both
 * provider tiers' accuracy plus their per-question outcomes (correct,
 * incorrect, unsuccessful — each with the answer and, for the latter two,
 * why it landed there), and their cross-provider agreement. Shared by
 * {@link useEvaluationRun} (polling a job in progress) and
 * {@link useLatestRunForSet} (CC-B4, a one-shot completed-run lookup) so
 * both render through the exact same view shape. */
const toEvaluationRunView = (response: GovernedEvaluationJobResponse): EvaluationRunView => {
  const result = response.result;
  return {
    status: response.status,
    error: response.error,
    isSettled: isSettled(response.status),
    contextSetId: result?.context_set_id ?? null,
    primary: result ? tierView(result.primary) : EMPTY_TIER,
    fallback: result?.fallback ? tierView(result.fallback) : null,
    crossProviderConsistency: result?.cross_provider_consistency ?? null,
    primaryItemsCompleted: response.progress.primary_completed,
    itemsTotal: response.progress.total,
    jobId: response.job_id,
    updatedAt: response.updated_at,
  };
};

/**
 * Poll a running governed accuracy job until it settles.
 *
 * `select` narrows the response to what the accuracy screen shows — see
 * {@link toEvaluationRunView}.
 */
export const useEvaluationRun = (jobId: string | null) => {
  const { data, error, status } = useQuery({
    queryKey: ["governed-evaluation", jobId],
    queryFn: () => getEvaluationStatus(jobId as string),
    enabled: !!jobId,
    refetchInterval: (query) => {
      // A persistent error (retries exhausted) is a dead end, not a blip —
      // polling it forever would just hammer the server on every tick with
      // no chance of a different answer.
      if (query.state.status === "error") return false;
      return isSettled(query.state.data?.status) ? false : POLL_INTERVAL_MS;
    },
    refetchIntervalInBackground: false,
    retry: (failureCount, queryError) => {
      const status = (queryError as AxiosError)?.response?.status;
      if (status === 404) return failureCount < NOT_FOUND_RETRIES;
      return isTransientError(queryError) && failureCount < TRANSIENT_ERROR_RETRIES;
    },
    // Shared with the auth-refresh retry path (auth-retry.helpers.ts) —
    // exponential backoff with jitter, not just exponential, for consistency
    // with the rest of the codebase's retry behavior.
    retryDelay: (attempt) => Math.min(30_000, computeBackoffDelayMs(1000, attempt)),
    select: toEvaluationRunView,
  });

  return {
    run: data ?? null,
    error,
    /** The job id doesn't exist server-side — stale, or from a wiped worker. */
    isNotFound: status === "error" && (error as AxiosError)?.response?.status === 404,
    /** Polling has given up (retries exhausted, if any applied) — a 404 is one
     * cause, but a persistent 5xx or network error is just as much of a dead
     * end: `refetchInterval` has already stopped polling either way, so there
     * is no future tick that could recover this job on its own. */
    isDeadEnd: status === "error",
  };
};

/** The company's last validated/run question set (CC-B3), if any — lets the
 * Test stage hydrate on mount instead of always starting from an empty
 * picker. 404 ("nothing saved yet") settles immediately, no retries. */
export const useLatestQuestionSet = () => {
  const { data, isFetching } = useQuery({
    queryKey: LATEST_QUESTION_SET_KEY,
    queryFn: getLatestQuestionSet,
    retry: (failureCount, queryError) =>
      (queryError as AxiosError)?.response?.status !== 404 && failureCount < NOT_FOUND_RETRIES,
  });
  // `isFetching` covers the refetch a validate/run success just invalidated,
  // not only the first load — hydrating from the stale cached value while
  // that fresh read is in flight would show the set that was just replaced.
  return { data: data ?? null, isFetching };
};

/**
 * The latest completed accuracy run for a context set (CC-B4) — a one-shot
 * lookup, not a poll (see {@link useEvaluationRun} for the in-progress
 * case): lets a fresh browser session, or another admin, see run history
 * instead of only what sessionStorage-backed tracking remembers in THIS tab.
 * 404 ("no completed run yet") settles immediately, no retries.
 */
export const useLatestRunForSet = (contextSetId: string | null) => {
  const { data, isFetched } = useQuery({
    queryKey: ["latest-run-for-set", contextSetId],
    queryFn: () => getLatestRunForSet(contextSetId as string),
    enabled: !!contextSetId,
    retry: (failureCount, queryError) =>
      (queryError as AxiosError)?.response?.status !== 404 && failureCount < NOT_FOUND_RETRIES,
    select: toEvaluationRunView,
  });
  // `isFetched` — not just `data` — is what a caller needs to tell "still
  // resolving" apart from "resolved, found nothing": both read as `null`
  // here, but `usePreviousRun`'s own gating (see ContextConsole.tsx)
  // needs to know specifically that THIS has already settled before
  // deciding there's really nothing to exclude.
  return { data: data ?? null, isFetched };
};

/**
 * The completed run right before `excludeJobId`, for this company — shown
 * alongside whatever run is currently on screen, for a before/after
 * comparison across a republish. Not scoped to a context set: a freshly
 * published one has at most one run of its own, so that filter would defeat
 * the point. A one-shot lookup, same 404-is-expected contract as
 * {@link useLatestRunForSet}. `excludeJobId` is the run currently on screen
 * (session-tracked or CC-B4's own fallback) when there is one — deliberately
 * NOT required to be non-null: with nothing on screen yet (right after a
 * republish, before the admin re-runs the test against the new set), the
 * backend drops the exclusion and returns the plain latest completed run
 * instead, so "last run's score" is always shown once any run exists, not
 * only once the current set has one of its own.
 *
 * `enabled` — the caller (ContextConsole.tsx) is expected to wait until
 * `excludeJobId`'s OWN resolution has actually settled before flipping this
 * on: without it, this fires once immediately with `excludeJobId: null`
 * (the company-wide fallback) and again moments later once the real
 * exclude-job-id resolves, briefly mislabeling the CURRENT run's own score
 * as "Previous run".
 */
export const usePreviousRun = (excludeJobId: string | null, enabled: boolean) => {
  const { data } = useQuery({
    queryKey: ["previous-run-for-set", excludeJobId],
    queryFn: () => getPreviousRun(excludeJobId),
    enabled,
    retry: (failureCount, queryError) =>
      (queryError as AxiosError)?.response?.status !== 404 && failureCount < NOT_FOUND_RETRIES,
    select: toEvaluationRunView,
  });
  return data ?? null;
};
